/*
  SPDX-License-Identifier: SEE LICENSE IN EULA.md
  SPDX-FileCopyrightText: 2020-2026 CYBERTEC PostgreSQL International GmbH <office@cybertec.at>
*/
import { ApiClient } from '../types/api/ApiClient';
import {
  JiraCustomerSupportComment,
  JiraCustomerSupportRequestComment,
  JiraCustomerSupportTicket,
  JiraCustomerSupportTicketRequest,
  JiraRequestType,
  JiraRequestTypeField,
} from '../types/support/JiraSupport';
import { getApiEndpoint } from '../utils/getApiEndpoint';
import { apiClientFactory } from '../utils/http-client/apiClientFactory';
import { axiosInstanceFactory } from '../utils/http-client/axiosInstanceFactory';
import { axiosJwtTokenInterceptor, axiosUnauthorizedInterceptor } from '../utils/http-client/axiosInterceptors';

class SupportJiraApiServiceClass {
  private api: ApiClient;

  constructor() {
    this.api = apiClientFactory(
      axiosInstanceFactory(
        { baseURL: getApiEndpoint() },
        [axiosJwtTokenInterceptor],
        [[undefined, axiosUnauthorizedInterceptor]],
      ),
    );
  }

  fetchJiraConfig = () => {
    return this.api.get<{ status: boolean; error?: string }>(`config/jira/status`);
  };

  fetchRequest = ({ id }: { id: string }) => {
    return this.api.get<any>(`jira/request/${id}`);
  };

  fetchRequestsWithPagination = ({
    requestStatus,
    take,
    skip,
  }: {
    requestStatus: 'all' | 'closed' | 'open';
    take: number;
    skip: number;
  }) => {
    return this.api.get<JiraCustomerSupportTicket[]>(`jira/requests`, {
      params: { requestStatus, take, skip },
    });
  };

  fetchRequestTypes = () => {
    return this.api.get<JiraRequestType[]>('jira/types');
  };

  fetchRequestTypeFields = ({ id }: { id: string }) => {
    return this.api.get<JiraRequestTypeField[]>(`jira/types/${id}/fields`);
  };

  fetchRequestComments = ({ id }: { id: string }) => {
    return this.api.get<any[]>(`jira/request/${id}/comments`);
  };

  createCustomerRequest = ({ data }: { data: JiraCustomerSupportTicketRequest }) => {
    return this.api.post<JiraCustomerSupportTicket>(`jira/request`, data);
  };

  createAttachment = ({ data }: { data: FormData }) => {
    return this.api.post(`jira/attachment`, data, { headers: { 'Content-Type': 'multipart/form-data' } });
  };

  createRequestComment = ({ id, body }: JiraCustomerSupportRequestComment & { id: string }) => {
    return this.api.post<JiraCustomerSupportComment>(`jira/request/${id}/comments`, { body });
  };
}

export const SupportJiraApiService = new SupportJiraApiServiceClass();
